import { isChildOfType } from "@faire/slate/lib/slotUtils";
import { tsv } from "@faire/slate/lib/tsv";
import React from "react";

import { RadioBlock } from "./RadioBlock";

interface RadioBlockGroupProps {
  /**
   * RadioBlock children. Only RadioBlock are affected by group level props. Other types are rendered as is.
   */
  children: React.ReactNode;

  /**
   * Grid layout:
   * - 1: Single column
   * - 2: Two columns
   * default: 1
   */
  columns?: 1 | 2;

  /**
   * **web**
   * The aria label of the radio block group.
   */
  "aria-label"?: string;

  /**
   * **web**
   * The test id of the radio block group.
   */
  "data-test-id"?: string;

  /**
   * **web**
   * Shared name for radio group. When defined, it will override the name of the children RadioBlock.
   */
  name?: string;

  /**
   * **web**
   * Controls the variant of the radio blocks in the group. When defined, it will override the variant of the children RadioBlock.
   */
  variant?: React.ComponentProps<typeof RadioBlock>["variant"];
}

/**
 * Layout container that manages spacing and optionally injects shared props for radio blocks.
 * {@link https://www.notion.so/faire/Radio-Block-2e82efb5c25a8002925bcca5cab92c6a | Community/RadioBlockGroup}
 */
export const RadioBlockGroup: React.FC<RadioBlockGroupProps> = ({
  children,
  columns = 1,
  name,
  variant,
  ...props
}) => {
  // We need to also consider potential children that isn't a RadioBlock component
  const processedChildren = React.Children.toArray(children).map((child) => {
    if (!isChildOfType(child, RadioBlock)) {
      return child;
    }

    const injectedProps: Partial<React.ComponentProps<typeof RadioBlock>> = {};

    if (variant !== undefined) {
      injectedProps.variant = variant;
    }

    if (name !== undefined) {
      injectedProps.name = name;
    }

    return React.cloneElement(child, injectedProps);
  });

  return (
    <div
      className={gridStyle.cls({ columns })}
      data-component-name="Community/RadioBlockGroup"
      role={variant === "multiselect" ? "group" : "radiogroup"}
      {...props}
    >
      {processedChildren}
    </div>
  );
};

const gridStyle = tsv({
  base: "grid w-full gap-3",
  variants: {
    columns: {
      1: "grid-cols-1",
      2: "grid-cols-2",
    },
  },
  defaultVariants: {
    columns: 1,
  },
});
